import Dialog from "../../components/Dialog";
import { Organization } from "../../services/organization/organizationModel";
import { useTranslation } from "react-i18next";
import SxForm from "../../forms/SxForm";
import SxField from "../../forms/fields/SxField";
import SubmitButton from "../../components/SubmitButton";
import ValidationErrors from "../../forms/ValidationErrors";
import { date, object, string } from "yup";
import { useState } from "react";
import { organizationService } from "../../services/organization/organizationService";
import useReload from "../../hooks/useReload";
import { loggerBuilder } from "../../services/logger";

interface Props {
  onClose(): void;
  organization: Organization;
}

// Exemple : tu ajoutes une fonction dans organizationService
const { inviteCandidatesByEmail } = organizationService();
const logger = loggerBuilder("invite-candidates-dialog");

const InviteCandidatesDialog = ({ onClose, organization }: Props) => {
  const { t } = useTranslation(["organization"]);
  const reload = useReload();
  const [loading, setLoading] = useState(false);

  return (
    <Dialog onClose={onClose} className="--branded">
      <>
        <div className="popup_head">
          <div className="popup_title">
            {t("organization:candidates-profiles.INVITE_CANDIDATES_TITLE")}
          </div>
        </div>

        <div className="popup_body">
          <p>
            {t("organization:candidates-profiles.labels.ORGANIZATION_NAME")}{" "}
            {organization.legalName}
          </p>
          <SxForm
            initialValues={{ emails: "", deadline: "" }}
            validationSchema={object({
              emails: string()
                .label(t("organization:candidates-profiles.labels.EMAILS"))
                .required(
                  t("organization:candidates-profiles.errors.EMAILS_REQUIRED"),
                )
                .test(
                  "valid-emails",
                  t("organization:candidates-profiles.errors.INVALID_EMAILS"),
                  (value) => {
                    if (!value) return false;
                    const emails = value
                      .split(/\r?\n/)
                      .map((e) => e.trim())
                      .filter(Boolean);
                    if (emails.length === 0) return false;
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return emails.every((e) => emailRegex.test(e));
                  },
                ),
              deadline: date()
                .nullable()
                .transform((_value, originalValue) => {
                  // Si le champ est vide ou nul, on retourne null
                  if (!originalValue) return null;
                  const parsedDate = new Date(originalValue);
                  return isNaN(parsedDate.getTime()) ? null : parsedDate;
                })
                .test(
                  "is-valid-date",
                  t("organization:candidates-profiles.errors.INVALID_DATE"),
                  (value) => !value || !isNaN(value.getTime()),
                )
                .label(t("organization:candidates-profiles.labels.DEADLINE")),
            })}
            onSubmit={async (values) => {
              const emails = values.emails
                .split(/\r?\n/)
                .map((e) => e.trim())
                .filter(Boolean);

              if (emails.length === 0) return;

              setLoading(true);
              try {
                await inviteCandidatesByEmail(
                  organization.id,
                  emails,
                  values.deadline,
                );
                reload();
                onClose();
              } catch (err) {
                logger.error(err);
              } finally {
                setLoading(false);
              }
            }}
          >
            <SxField
              name="emails"
              as="textarea"
              placeholder={t(
                "organization:candidates-profiles.PLACEHOLDER_EMAILS",
              )}
              label={t("organization:candidates-profiles.labels.EMAILS")}
            />

            <SxField
              name="deadline"
              as="date"
              label={t("organization:candidates-profiles.labels.DEADLINE")}
            />

            <ValidationErrors />

            <div className="form_footer --txt--center">
              <SubmitButton className="btn" disabled={loading}>
                {t("organization:candidates-profiles.SEND_INVITATIONS")}
              </SubmitButton>
            </div>
          </SxForm>
          <div className="cblock">
            <p className="body--40">
              {t(
                "organization:candidates-profiles.INVITE_CANDIDATES_EXPLANATION",
              )}
            </p>
          </div>
        </div>
      </>
    </Dialog>
  );
};

export default InviteCandidatesDialog;
